import React, { useEffect, useRef, useState } from 'react';
import useBoardContentWidgetData from '../../../hooks/useBoardContentWidgetData';
import useBoardContentWidget from '../../../hooks/useBoardContentWidget';
import { useInternationalization } from '@progress/kendo-react-intl';
import { useParams } from 'react-router-dom';
import useClickOutside from '../../../../../common/hooks/useClickOutside';
import { MODAL, POPUP } from '../../../../../redux/types';
import { useDispatch } from 'react-redux';

import { IBoardProperty } from '../../../types/IBoardProperty';
import { IBoardMeasure } from '../../../types/IBoardMeasure';
import { IBoardNote } from '../../../types/IBoardNote';
import { IBoardObjectNode } from '../../../types/IBoardObjectNode';
import { ISummarySetup } from './types/ISummarySetup';
import { ISummaryValue } from './types/ISummaryValueItem';
import { ISummaryDefinition } from './types/ISummaryDefinition';
import { ICurrency } from '../../../types/ICurrency';
import { IWidgetCombinedData } from '../../../../services/types/IWidgetCombinedData';
import { ICashflow } from '../../../types/ICashflow';
import { WidgetProps } from '../../../types/IWidgetProps';

import WidgetWrapper from '../widgetWrapper';
import ContentLoading from '../../../../ui/loaders/contentLoading/contentLoading';

import './summaryWidget.scss';

const SummaryWidget: React.FC<WidgetProps> = ({
                                                widget,
                                                selectedObject,
                                                selectedPeriod,
                                                selectedCurrency,
                                                boardData,
                                                scenarios,
                                                customisation,
                                                notes,
                                                moveWidget,
                                                removeWidget,
                                                templateProperties,
                                                handleLink,
                                                content,
                                                homeContent,
                                                widgetView,
                                                handlePinWidget,
                                                handleUnpinWidget,
                                                handleUnpinTrigger,
                                                unpinId,
                                              }) => {
  const [setup, setSetup] = useState<ISummarySetup>();
  const [values, setValues] = useState<ISummaryValue[]>([]);
  const [summaryContent, setSummaryContent] = useState<ISummaryDefinition[]>([]);
  const [widgetCurrencies, setWidgetCurrencies] = useState<ICurrency[]>([]);
  const [localCurrency, setLocalCurrency] = useState<ICurrency>();
  const [nodeCurrency, setNodeCurrency] = useState<string>();
  const [summaryNotes, setSummaryNotes] = useState<IBoardNote[]>([]);
  const [notesShown, setNotesShown] = useState<any>();
  const [nodes, setNodes] = useState<IBoardObjectNode[]>([]);
  const [nodeGroups, setNodeGroups] = useState<IBoardObjectNode[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const { defineNodes, fetchCombinedData, generateCurrencies } = useBoardContentWidgetData();
  const { widgetSetup } = useBoardContentWidget();
  const intl = useInternationalization();
  const params = useParams();
  const notesRef = useRef(null);
  const dispatch = useDispatch();

  const measuresForFetch = () => {
    let measures: IBoardMeasure[] = [];
    setup?.definition?.forEach((def) => {
      if (def.measureId && !measures.find((m) => m.id === def.measureId)) measures.push({ id: def.measureId });
    });
    return measures;
  };

  const staticPropertiesForFetch = () => {
    let propertiesForFetch: any = [];
    setup?.definition?.forEach((def) => {
      if (
        def.templatePropertyId &&
        !propertiesForFetch.find((item) => {
          return item.templatePropertyId === def.templatePropertyId;
        })
      )
        propertiesForFetch.push({ templatePropertyId: def.templatePropertyId });
    });
    return propertiesForFetch;
  };

  const initializeItems = (data: IWidgetCombinedData | null) => {


    const values: ISummaryValue[] = [];

    if (setup) {
      for (let i = 0; i < setup.definition.length; i++) {
        let definition = setup.definition[i];

        if (definition && definition.measureId) {
          let measureId = definition.measureId;
          let measure = findMeasure(measureId);
          let value = data?.measureValues.find((item) => item.measureId === measureId);

          let amount = null;
          let formatAmount = '';

          if (measure?.dataType === 'date') {
            amount = value?.dateValue ? new Date(value.dateValue) : null;
            formatAmount = amount ? intl.formatDate(amount, 'd') : '';
          } else {
            amount = value?.amount ? value.amount / definition.amountsIn : 0;
            formatAmount = amount ? intl.toString(amount, (measure.isPercent ? 'p' : 'n') + definition.scale) : '0';
          }

          values.push({
            name: definition.name,
            scale: definition.scale,
            isPercent: measure && measure.isPercent,
            amount: amount,
            formatAmount: formatAmount,
            measureId: measureId,
            measure: measure,
            templatePropertyId: null,
            viewCashflows: definition.viewCashflows,
            valueId: value?.id || 0,
            scenarioId: value?.scenarioId || 0,
            nodeId: value?.nodeId || 0,
          });
        }

        if (definition && definition.templatePropertyId) {
          let staticProperty = findProperty(definition.templatePropertyId);
          let staticValue = data?.staticValues.find((item) => item.templatePropertyId === definition.templatePropertyId);

          values.push({
            name: definition.name,
            scale: definition.scale,
            isPercent: staticProperty && staticProperty.dataType === 'percent',
            amount: staticValue?.rawValue,
            formatAmount: staticProperty && staticValue ? formatStaticPropertyValue(staticProperty, staticValue.rawValue) : '',
            measureId: null,
            measure: null,
            templatePropertyId: definition.templatePropertyId,
            viewCashflows: false,
            valueId: 0,
            scenarioId: 0,
            nodeId: staticValue?.nodeId || 0,
          });
        }
      }
    }

    return values;
  };

  const formatStaticPropertyValue = (staticProperty: IBoardProperty, value) => {
    if (!value) return '';

    if (staticProperty.dataType === 'number') return intl.toString(Number.parseFloat(value), `n${staticProperty.scale}`);

    if (staticProperty.dataType === 'date') return intl.toString(new Date(value), 'd');

    if (staticProperty.dataType === 'percent') return intl.toString(Number.parseFloat(value), `p${staticProperty.scale}`);

    return value;
  };

  const filterNotes = (notes: IBoardNote[]) => {
    let summaryNotes: IBoardNote[] = [];
    for (let i = 0; i < notes.length; i++) {
      const measures = notes[i] && notes[i].measures;
      if (measures) {
        for (let j = 0; j < measures.length; j++) {
          const measureId = measures[j].measureId;
          summaryContent.map((summaryItem) => {
            if (summaryItem.measureId === measureId && !summaryNotes.includes(notes[i])) summaryNotes.push(notes[i]);
          });
        }
      }
    }
    if (summaryContent.length > 0) {
      extractNotesForSummaryItems(summaryNotes);
    }

    setSummaryNotes(summaryNotes);
  };

  const extractNotesForSummaryItems = (notes: IBoardNote[]) => {
    setSummaryContent(
      summaryContent.map((summaryItem) => {
        let itemNotes: IBoardNote[] = [];
        const measureId = summaryItem.measureId;
        notes.forEach((note) => {
          const noteMeasureIds = note?.measures?.map((m) => m.measureId);
          if (measureId && noteMeasureIds?.includes(measureId)) {
            itemNotes.push(note);
          }
        });
        return { ...summaryItem, notes: itemNotes, hasNotes: itemNotes.length > 0 };
      }),
    );
  };

  const findMeasure = (measureId: number) => {
    if (boardData && boardData.measures) {
      return boardData.measures.find((item) => item.id === measureId);
    }
  };

  const findProperty = (templatePropertyId: number) => {
    if (boardData && boardData.staticProperties) {
      return boardData.staticProperties.find((item) => item.id === templatePropertyId);
    }
  };

  const getCashflowInfo = (value: any): ICashflow => {
    return {
      nodeId: value.nodeId,
      scenarioId: value.scenarioId,
      valueId: value.valueId,
      valueInfo: {
        measure: value.measure,
        amount: {
          rowAmount: value?.amount,
          isPercent: value.isPercent,
          scale: value.scale,
        },
      },
    };
  };

  const openCashFlowDialog = (value: ISummaryValue) => {
    let cashflow: ICashflow = getCashflowInfo(value);

    dispatch({
      type: MODAL.SWITCH_CASH_FLOW_MODAL,
      payload: {
        dataItem: { ...value, currency: nodeCurrency },
        cashFlowInfo: cashflow,
        investorObjectId: widget.investorObjectId,
        value: value.formatAmount,
        boardId: params.boardId || boardData.boardId,
        selectedCurrency: localCurrency,
      },
    });
  };

  const handleLocalCurrencyChange = (e) => {
    setLocalCurrency(e.value);

    if (selectedPeriod) {
      setLoading(true);
      fetchCombinedData(widget, nodes, nodeGroups, measuresForFetch(), staticPropertiesForFetch(), selectedPeriod, e.value.currency || '')
        .then((data) => {

          const values = initializeItems(data);

          setValues(values);
          setLoading(false);
        })
        .catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    }
  };

  const clickOutsideHandler = () => {
    setNotesShown({ showNote: '' });
  };

  const renderCashflowItem = (summaryItem, amount, value) => {
    if (summaryItem.propertyType === 'website') {
      return (
        <a className='link' target='_blank'
           href={`${amount}`.includes('https://') || `${amount}`.includes('http://') ? `${amount}` : `https://${amount}`}>
          {amount || ''}
        </a>
      );
    } else {
      return (
        <div className='summary-widget-value pointer summary-widget-value-link'
             onClick={() => openCashFlowDialog(value)}>
          {amount}
        </div>
      );
    }
  };

  const renderSummaryItem = (summaryItem, amount) => {
    if (summaryItem.propertyType === 'website') {
      return (
        <a className='link' target='_blank'
           href={`${amount}`.includes('https://') || `${amount}`.includes('http://') ? `${amount}` : `https://${amount}`}>
          {amount || ''}
        </a>
      );
    } else {
      return <span className='summary-widget-value'>{amount}</span>;
    }
  };

  useClickOutside(notesRef, clickOutsideHandler);

  useEffect(() => {
    let wSetup = widgetSetup(widget);
    if (wSetup) {
      if (templateProperties) {
        wSetup.definition = wSetup.definition.map((summaryItem) => {
          if (summaryItem.type === 'property') {
            let property = templateProperties.find((p) => p.id === summaryItem.templatePropertyId);
            return {
              ...summaryItem,
              propertyType: property?.type || '',
              propertyDataType: property?.dataType || 'text',
            };
          } else return { ...summaryItem };
        });
      }
      setSetup(wSetup);
      setSummaryContent(wSetup.definition);
    }
  }, [widget, templateProperties]);

  useEffect(() => {
    if (selectedObject && selectedPeriod && selectedCurrency && setup) {
      setLocalCurrency(selectedCurrency);

      const { nodes, groups } = defineNodes(widget, selectedObject);
      setNodes(nodes);
      setNodeGroups(groups);

      if (nodes && nodes[0] && nodes[0].currency) {
        setNodeCurrency(nodes[0].currency);
      }

      if (scenarios) {
        let currencies = generateCurrencies(widget, nodes, scenarios);
        setWidgetCurrencies(currencies);
      }

      fetchCombinedData({
        ...widget,
        investorObjectId: widget.investorObjectId || parseInt(params.investorId),
      }, nodes, groups, measuresForFetch(), staticPropertiesForFetch(), selectedPeriod, selectedCurrency.currency || '')
        .then((data) => {

          const values = initializeItems(data);
          setValues(values);
          setLoading(false);
        })
        .catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    }
  }, [selectedObject, selectedPeriod, selectedCurrency, setup, widget]);

  useEffect(() => {
    if (notes) {
      filterNotes(notes);
    }
  }, [notes, widget]);

  return window.innerWidth < 960 && setup?.hideOnMobile ? null : (
    <>
      <WidgetWrapper
        widget={widget}
        localCurrency={localCurrency}
        widgetCurrencies={widgetCurrencies}
        handleLocalCurrencyChange={handleLocalCurrencyChange}
        notes={summaryNotes}
        customisation={customisation}
        moveWidget={moveWidget}
        removeWidget={removeWidget}
        selectedObject={selectedObject}
        selectedCurrency={nodeCurrency}
        handleLink={handleLink}
        content={content}
        homeContent={homeContent}
        widgetView={widgetView}
        handleUnpinFromBoardCallback={handleUnpinWidget}
        handlePinCallback={handlePinWidget}
        handleUnpin={handleUnpinTrigger}
        unpinId={unpinId}
      >
        {loading ? (
          <ContentLoading />
        ) : (
          <div className='summary-widget-wrapper'>
            {summaryContent.map((summaryItem, index) => {
              const value = values[index];
              const amount = value?.formatAmount;

              return (
                <div
                  className={`summary-item-wrapper ${summaryItem.propertyDataType === 'long_text' ? 'summary-long-text-wrapper' : ''}`}
                  key={index}>
                  <span className='summary-item-name'>
                    <div className='summary-item-name-label'>{summaryItem.name}</div>
                    {/*{summaryItem.hasNotes && (*/}
                    {/*  <div className='notes-toggler'>*/}
                    {/*    <Icon*/}
                    {/*      name={`${notesShown?.showNote === summaryItem.$$hashKey ? 'far' : 'fas'} fa-circle`}*/}
                    {/*      className='notes-toggler-icon pointer'*/}
                    {/*      onClick={() => toggleNotes(summaryItem.$$hashKey || '')}*/}
                    {/*      id={summaryItem.$$hashKey}*/}
                    {/*    />*/}
                    {/*    {notesShown?.showNote === summaryItem.$$hashKey && (*/}
                    {/*      <div className='notes-content' ref={notesRef}>*/}
                    {/*        {summaryItem.notes &&*/}
                    {/*          summaryItem.notes.map((note, key) => (*/}
                    {/*            <p key={key}>*/}
                    {/*              {note.symbol} {note.note}*/}
                    {/*            </p>*/}
                    {/*          ))}*/}
                    {/*      </div>*/}
                    {/*    )}*/}
                    {/*  </div>*/}
                    {/*)}*/}
                  </span>
                  {value?.viewCashflows ? renderCashflowItem(summaryItem, amount, value) : renderSummaryItem(summaryItem, amount)}
                </div>
              );
            })}
          </div>
        )}
      </WidgetWrapper>
    </>
  );
};

export default SummaryWidget;
